import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import { path, pathOr } from 'ramda'
import Select from '../../../atoms/Select/Select'
import TextField from '../../../atoms/TextField/TextField'
import Button from '../../../atoms/Button/Button'
import styles from './EasyPayBeneficiary.module.scss'
import CurrencyField from '../../CurrencyField/CurrencyField'
import CurrencyText from '../../../atoms/Typography/CurrencyText/CurrencyText'
import TextInput from 'views/components/atoms/TextField/TextInput'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

interface IEasyPayBeneficiaryProps {
  beneficiaryTagGroups: Record<string, any[]>
  beneficiaryTagGroupsForSelect: { value: string; label: string }[]
  state: {
    easyPayReference: string | undefined
    beneficiaryTag: string
    amount: number
  }
  setState: (state: Partial<IEasyPayBeneficiaryProps['state']>) => void
  onNewBeneficiaryAdded: (beneficiary: { type: string; value: any }) => void
  fetchEasyPayData: (reference: string) => void
  easyPayData: Record<string, any>
  unallocatedAmount: number
}

const EasyPayBeneficiary = ({
  beneficiaryTagGroups,
  beneficiaryTagGroupsForSelect,
  state,
  setState,
  onNewBeneficiaryAdded,
  fetchEasyPayData,
  easyPayData,
  unallocatedAmount,
}: IEasyPayBeneficiaryProps) => {
  const [tagGroupSelection, setTagGroupSelection] = useState<string | null>(null)
  const [nextStep, setNextStep] = useState<'benficiaryTagGroup' | 'beneficiaryTag' | 'easyPayReference' | 'amount'>(
    'benficiaryTagGroup',
  )
  const [error, setError] = useState<string | undefined>(undefined)
  const [isRequestingEasyPayData, setIsRequestingEasyPayData] = useState(false)
  const [currentEasyPayReference, setCurrentEasyPayReference] = useState<string | undefined>(undefined)

  const isEasyPayReferenceDataValid = (data: any) => pathOr(false, ['request', 'success'], data)

  useEffect(() => {
    const data = pathOr({}, [state.easyPayReference ?? ''], easyPayData)
    setIsRequestingEasyPayData(false)

    if (isEasyPayReferenceDataValid(data)) {
      setNextStep('amount')
    } else {
      setError(path(['request', 'response_message'], data))
    }
  }, [easyPayData, state.easyPayReference, onNewBeneficiaryAdded, state])

  const renderNextField = () => {
    if (nextStep === 'benficiaryTagGroup') {
      return (
        <Select
          defaultMenuIsOpen
          onChange={(val: any) => {
            if (beneficiaryTagGroups[val.value].length === 1) {
              setState({ beneficiaryTag: val.value })
              setNextStep('easyPayReference')
            } else {
              setTagGroupSelection(val.value)
              setNextStep('beneficiaryTag')
            }
          }}
          label="EasyPay beneficiary type"
          name="beneficiaryTagGroups"
          placeholder="Select"
          options={beneficiaryTagGroupsForSelect}
        />
      )
    }

    if (nextStep === 'beneficiaryTag') {
      return (
        <Select
          menuIsOpen={!state.beneficiaryTag}
          onChange={({ value }: any) => {
            setState({ beneficiaryTag: value })
            setNextStep('easyPayReference')
          }}
          name="beneficiaryTag"
          label="EasyPay beneficiary tag"
          labelHint="This will appear on the lease statement"
          placeholder="Select"
          // @ts-expect-error ts-migrate(2538) FIXME: Type 'null' cannot be used as an index type.
          options={beneficiaryTagGroups[tagGroupSelection]}
        />
      )
    }

    if (nextStep === 'easyPayReference') {
      return (
        <TextField
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name="easyPayReference"
              onChange={({ target }: any) => setCurrentEasyPayReference(target.value)}
              placeholder=""
              ref={(ref: any) => {
                ref?.focus()
              }}
            />
          }
          label="Lesaka EasyPay reference"
          error={error}
        />
      )
    }

    if (nextStep === 'amount') {
      return (
        <CurrencyField
          name="amount"
          label="Amount (optional)"
          labelHint="Leave blank to set the amount later"
          placeholder=""
          value={state.amount}
          onChange={({ target }: any) => setState({ amount: target.value || 0 })}
          error={
            state.amount > unallocatedAmount ? (
              <span>
                Can&apos;t exceed unallocated amount of <CurrencyText>{unallocatedAmount}</CurrencyText>
              </span>
            ) : (
              false
            )
          }
        />
      )
    }
  }

  const isStateValid = () => state.beneficiaryTag && state.easyPayReference

  const buttonClasses = cx({
    [styles['add-button']]:
      (nextStep === 'easyPayReference' && error) ?? (nextStep === 'amount' && state.amount > unallocatedAmount),
    [styles['reference-button']]: nextStep === 'easyPayReference' && !error,
    [styles['amount-button']]: nextStep === 'amount',
  })

  return (
    <div className={styles.root}>
      {isRequestingEasyPayData ? (
        <p>Validating EasyPay reference...</p>
      ) : (
        <div className={styles.form}>
          <div className={styles.input}>{renderNextField()}</div>

          {nextStep === 'easyPayReference' && (
            <Button
              disabled={!currentEasyPayReference}
              size="sm"
              onClick={() => {
                if (!currentEasyPayReference) return
                setState({ easyPayReference: currentEasyPayReference })
                fetchEasyPayData(currentEasyPayReference)
                setIsRequestingEasyPayData(true)
              }}
              className={buttonClasses}
            >
              Next
            </Button>
          )}

          {nextStep === 'amount' && (
            <Button
              disabled={!isStateValid()}
              size="sm"
              onClick={() => onNewBeneficiaryAdded({ type: 'EasyPayBeneficiary', value: state })}
              className={buttonClasses}
            >
              Save
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default EasyPayBeneficiary
